import React from 'react'
import Time24Input from './Time24Input'

function chileShortcut(daysOffset = 0) {
  // Chile UTC-4 (sin DST entre abril-septiembre). Usar fecha local del navegador.
  // Asumimos que el agente trabaja desde Chile. Suficiente para affordances de UI.
  const d = new Date()
  d.setDate(d.getDate() + daysOffset)
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

/**
 * DayPickerModal — componente standalone usado por:
 * 1. NotesSection.jsx (variantes 'no_contesta', 'si_contesta', 'agendar')
 * 2. TodoListView.jsx vía click en celda DÍA (variante 'day_picker')
 *
 * Props:
 *   open, onClose
 *   source: 'no_contesta' | 'si_contesta' | 'agendar' | 'day_picker'
 *   currentDay: number | null  (día actual del contacto)
 *   onConfirm(selectedDay: number | null, asDate: string, asTime: string)
 *   asDate, setAsDate, asTime, setAsTime  (controlled por padre cuando source != 'day_picker')
 *   lockDate: boolean  (true cuando source='day_picker' — date input disabled)
 *   initialDay: number | null  (opcional — si se provee, pre-selecciona el día al abrir)
 */
export default function DayPickerModal({
  open,
  onClose,
  source,
  currentDay,
  onConfirm,
  asDate = '',
  setAsDate = () => {},
  asTime = '',
  setAsTime = () => {},
  lockDate = false,
  initialDay = null,
}) {
  const [selectedNoContestaDay, setSelectedNoContestaDay] = React.useState('')

  // Reset state al abrir. Si initialDay está provisto, pre-selecciona ese valor.
  React.useEffect(() => {
    if (open) {
      if (initialDay !== null && initialDay !== undefined) {
        setSelectedNoContestaDay(String(initialDay))
      } else {
        setSelectedNoContestaDay('')
      }
    }
  }, [open, source, initialDay])

  if (!open) return null

  const isNoContesta = source === 'no_contesta'
  const isDayPicker = source === 'day_picker'
  const isPending = isNoContesta && selectedNoContestaDay === ''
  const isConfirmed = (isNoContesta || isDayPicker) && selectedNoContestaDay !== ''
  const stdDays = [0, 2, 4, 8, 15, 30, 60]
  const currentDayNum = currentDay != null && currentDay !== '' ? Number(currentDay) : null
  const showDynamicOption = currentDayNum != null && !stdDays.includes(currentDayNum)

  // selectDisabled: cuando es agendar (sin día), dropdown bloqueado.
  const selectDisabled = source === 'agendar'

  const containerCls = isNoContesta
    ? (isPending
        ? 'mt-3 p-4 rounded-lg border-2 border-red-400 bg-red-50 dark:bg-red-900/15 ring-2 ring-red-300 animate-nocontesta-pulse'
        : 'mt-3 p-4 rounded-lg border-2 border-green-400 bg-green-50 dark:bg-green-900/15 ring-2 ring-green-300 shadow-[0_0_0_6px_rgba(34,197,94,0.20)]')
    : isDayPicker
    ? 'mt-3 p-4 rounded-lg border-2 border-blue-300 bg-blue-50 dark:bg-blue-900/15'
    : 'mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 space-y-2'

  const labelNuevoCls = 'text-base text-gray-700 dark:text-gray-300 w-28'
  const selectBorderCls = 'flex-1 px-3 py-2 border-2 border-gray-300 rounded-lg ' +
    'focus:border-blue-500 focus:outline-none dark:bg-gray-800 dark:text-white dark:border-gray-700'

  const headerText = isNoContesta
    ? 'No Contesta — Fecha AS'
    : source === 'si_contesta'
    ? 'Si Contesta — Fecha AS'
    : source === 'si_whatsapp'
    ? 'Si WSP — Fecha AS'
    : source === 'agendar'
    ? 'Agendar — Fecha AS'
    : 'Cambiar día del ciclo'

  const headerBg = isDayPicker
    ? 'from-blue-600 to-blue-700'
    : 'from-orange-600 to-orange-700'

  const confirmDisabled = isDayPicker
    ? selectedNoContestaDay === ''
    : !asDate || (isNoContesta && selectedNoContestaDay === '')

  const handleConfirmClick = () => {
    const day = selectedNoContestaDay !== ''
      ? parseInt(selectedNoContestaDay, 10)
      : null
    onConfirm(day, asDate, asTime)
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center">
      <div className="bg-gray-100 dark:bg-surface-dark rounded-lg shadow-2xl max-w-lg w-full mx-4">
        <div className={`bg-gradient-to-r ${headerBg} text-white px-6 py-4 rounded-t-lg`}>
          <h3 className="text-lg font-semibold">{headerText}</h3>
        </div>
        <div className="p-6">
          {/* Date + Time input (oculto/bloqueado cuando day_picker) */}
          {!isDayPicker && (
            <>
              <div className="mb-2 flex gap-2 flex-wrap">
                <button type="button" onClick={() => setAsDate(chileShortcut(0))}
                  className="px-3 py-1.5 text-sm bg-blue-100 hover:bg-blue-200 dark:bg-blue-900/40 dark:hover:bg-blue-900/60 text-blue-800 dark:text-blue-200 rounded-md border border-blue-300 dark:border-blue-700">
                  Hoy
                </button>
                <button type="button" onClick={() => setAsDate(chileShortcut(1))}
                  className="px-3 py-1.5 text-sm bg-blue-100 hover:bg-blue-200 dark:bg-blue-900/40 dark:hover:bg-blue-900/60 text-blue-800 dark:text-blue-200 rounded-md border border-blue-300 dark:border-blue-700">
                  Mañana
                </button>
                <button type="button" onClick={() => setAsDate(chileShortcut(7))}
                  className="px-3 py-1.5 text-sm bg-blue-100 hover:bg-blue-200 dark:bg-blue-900/40 dark:hover:bg-blue-900/60 text-blue-800 dark:text-blue-200 rounded-md border border-blue-300 dark:border-blue-700">
                  +7 días
                </button>
                <button type="button" onClick={() => setAsDate(chileShortcut(30))}
                  className="px-3 py-1.5 text-sm bg-blue-100 hover:bg-blue-200 dark:bg-blue-900/40 dark:hover:bg-blue-900/60 text-blue-800 dark:text-blue-200 rounded-md border border-blue-300 dark:border-blue-700">
                  +1 mes
                </button>
              </div>
              <div className="flex gap-3">
                <input
                  type="date"
                  value={asDate}
                  onChange={(e) => setAsDate(e.target.value)}
                  className="flex-1 px-3 py-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none dark:bg-gray-800 dark:text-white dark:border-gray-700"
                  autoFocus
                />
                {(source === 'agendar' || source === 'si_contesta' || source === 'si_whatsapp' || source === 'no_contesta') && (
                  <Time24Input
                    key={source}
                    value={asTime}
                    onChange={setAsTime}
                    placeholder="HH:MM"
                    className="w-32 px-3 py-3 border border-gray-300 rounded-lg text-center focus:border-blue-500 focus:outline-none dark:bg-gray-800 dark:text-white dark:border-gray-700"
                  />
                )}
              </div>
            </>
          )}
          {isDayPicker && (
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Solo cambia el día del ciclo. La fecha de seguimiento no se modifica.
            </div>
          )}

          {/* Sección Día */}
          <div className={containerCls}>
            <div className="flex items-center gap-2">
              <span className="text-base text-gray-700 dark:text-gray-300 w-28">Dia actual:</span>
              <span className="text-base font-medium text-gray-700 dark:text-gray-300">
                {currentDayNum != null ? '(' + currentDayNum + ')' : 'vacio'}
              </span>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <span className={labelNuevoCls}>Nuevo dia:</span>
              <select
                value={selectedNoContestaDay}
                onChange={(e) => setSelectedNoContestaDay(e.target.value)}
                disabled={selectDisabled}
                className={selectBorderCls}
              >
                <option value="">-</option>
                <option value="0">Dia 0</option>
                <option value="2">Dia 2</option>
                <option value="4">Dia 4</option>
                <option value="8">Dia 8</option>
                <option value="15">Dia 15</option>
                <option value="30">Dia 30</option>
                <option value="60">Dia 60</option>
                {showDynamicOption && (
                  <option value={String(currentDayNum)}>{'Dia ' + currentDayNum + ' (actual)'}</option>
                )}
              </select>
            </div>
            {isNoContesta && (
              <div className={isPending ? 'mt-2 text-red-700 dark:text-red-300 text-sm' : 'mt-2 text-green-700 dark:text-green-300 text-sm'}>
                {isPending ? '⚠️ Confirma un dia para continuar' : '✓ Dia confirmado'}
              </div>
            )}
            {isDayPicker && isConfirmed && (
              <div className="mt-2 text-blue-700 dark:text-blue-300 text-sm">✓ Dia listo para guardar</div>
            )}
          </div>
        </div>
        <div className="px-6 pb-6 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-5 py-2.5 text-gray-700 dark:text-gray-200 bg-gray-200 dark:bg-gray-700 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
          >
            Cancelar
          </button>
          <button
            onClick={handleConfirmClick}
            disabled={confirmDisabled}
            title={confirmDisabled && isNoContesta ? 'Confirma un dia para continuar' : ''}
            className={'px-5 py-2.5 text-white rounded-lg ' + (
              isDayPicker
                ? 'bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 disabled:cursor-not-allowed'
                : 'bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 disabled:cursor-not-allowed'
            )}
          >
            Confirmar
          </button>
        </div>
      </div>
    </div>
  )
}
